import React, { useState, useEffect } from 'react';
import { apiFetch } from './utils/api';

function MarketingAnalytics() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => { loadReport(); }, []);

  const loadReport = async () => {
    try {
      const res = await apiFetch('/api/marketing/analytics/send-report');
      if (!res.ok) throw new Error(`Failed to load send report: ${res.status}`);
      const data = await res.json();
      setReport(data);
    } catch (err) {
      console.error('Error loading send report:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const statusBadge = (status) => {
    switch (status) {
      case 'sent': return '🟢 Sent';
      case 'failed': return '🔴 Failed';
      default: return `⚪ ${status}`;
    }
  };

  const audienceLabel = (audience) => {
    switch (audience) {
      case 'commercial': return '🏢 Commercial';
      case 'domestic': return '🏠 Domestic';
      default: return audience || '—';
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-center text-gray-400">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-400 mx-auto mb-3" />
        Loading send report...
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 text-center">
        <p className="text-red-500 mb-2">Error loading report</p>
        <p className="text-sm text-gray-500">{error}</p>
        <button onClick={loadReport} className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600">Retry</button>
      </div>
    );
  }

  const { campaigns = [], summary = {} } = report || {};

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-lg shadow p-4 text-center">
          <div className="text-2xl font-bold text-gray-800">{summary.total_campaigns || 0}</div>
          <div className="text-sm text-gray-500">Campaigns Sent</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 text-center">
          <div className="text-2xl font-bold text-green-600">{summary.total_sent || 0}</div>
          <div className="text-sm text-gray-500">Emails Sent</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 text-center">
          <div className="text-2xl font-bold text-red-600">{summary.total_errors || 0}</div>
          <div className="text-sm text-gray-500">Errors</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 text-center">
          <div className="text-2xl font-bold text-blue-600">{summary.commercial_count || 0}</div>
          <div className="text-sm text-gray-500">🏢 Commercial</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 text-center">
          <div className="text-2xl font-bold text-orange-600">{summary.domestic_count || 0}</div>
          <div className="text-sm text-gray-500">🏠 Domestic</div>
        </div>
      </div>

      {/* Campaign Send Log */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-4 py-3 border-b">
          <h2 className="text-lg font-semibold text-gray-800">📋 Campaign Send Report</h2>
        </div>

        {campaigns.length === 0 ? (
          <div className="p-8 text-center text-gray-400">
            <p className="text-3xl mb-2">📭</p>
            <p>No campaigns have been sent yet.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Campaign</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Topic</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Audience</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-600">Sent To</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-600">Errors</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Sent Date</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {campaigns.map((c) => (
                  <tr key={c.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-800">{c.name}</td>
                    <td className="px-4 py-3 text-gray-600">{c.topic}</td>
                    <td className="px-4 py-3">{audienceLabel(c.audience)}</td>
                    <td className="px-4 py-3">{statusBadge(c.status)}</td>
                    <td className="px-4 py-3 text-right font-medium text-green-600">{c.sent_count}</td>
                    <td className="px-4 py-3 text-right">
                      {c.error_count > 0 ? (
                        <span className="text-red-600 font-medium">{c.error_count}</span>
                      ) : (
                        <span className="text-gray-400">0</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{formatDate(c.sent_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default MarketingAnalytics;
